import { MonthlyOpex, MonthlyOpexData, Year, OpexType, OpexItem } from '../types/opex';

/**
 * MonthlyOpex (백엔드 형식)를 MonthlyOpexData (프론트엔드 형식)로 변환 (3단계 지원)
 */
export const convertMonthlyOpexToData = (monthlyOpex: MonthlyOpex): MonthlyOpexData => {
  const processItems = (items: OpexItem[]) => {
    // 3단계 계층 구조 재구성
    const buildHierarchy = (itemList: OpexItem[], parentId: number | null = null, level: number = 0): OpexItem[] => {
      return itemList
        .filter(item => item.parentId === parentId && item.level === level)
        .map(item => {
          // 자식 항목들 재귀적으로 찾기
          const children = buildHierarchy(itemList, item.id, level + 1);
          
          const resultItem = {
            ...item,
            children: children.length > 0 ? children : undefined,
            isCategory: item.isCategory || children.length > 0
          };
          return resultItem;
        });
    };
    
    return buildHierarchy(items);
  };
  
  const indirectItems = monthlyOpex.opexItems.filter(item => item.type === OpexType.INDIRECT);
  const directItems = monthlyOpex.opexItems.filter(item => item.type === OpexType.DIRECT);
  
  const indirect = processItems(indirectItems);
  const direct = processItems(directItems);
  
  return {
    month: monthlyOpex.month,
    employeeCount: monthlyOpex.employeeCount,
    confirmed: monthlyOpex.confirmed,
    indirect,
    direct
  };
};

/**
 * Year (백엔드 형식)를 MonthlyOpexData[] (프론트엔드 형식)로 변환
 */
export const convertYearToData = (yearlyData: Year): MonthlyOpexData[] => {
  const result: MonthlyOpexData[] = [];
  
  for (let month = 1; month <= 12; month++) {
    const monthData = yearlyData.months.find(m => m.month === month);
    
    if (monthData) {
      result.push(convertMonthlyOpexToData(monthData));
    } else {
      result.push({
        month,
        employeeCount: 0,
        confirmed: false,
        indirect: [],
        direct: []
      });
    }
  }
  
  return result;
};

/**
 * MonthlyOpexData (프론트엔드 형식)를 백엔드 저장 형식으로 변환 (3단계 지원)
 * 계층 구조를 백엔드가 예상하는 형식으로 변환
 */
export const convertDataToUpdateFormat = (data: MonthlyOpexData) => {
  // 3단계 계층 구조를 그대로 전송 (백엔드에서 flattenHierarchy로 처리)
  const processHierarchicalData = (items: OpexItem[]) => {
    const processItem = (item: OpexItem, index: number, parentLevel: number = -1): any => {
      const hasValidCategory = item.category?.trim();
      const hasAmount = item.amount !== undefined && item.amount !== null && item.amount >= 0;
      const hasChildren = item.children && item.children.length > 0;
      
      // 유효성 검사: 카테고리명이 있고, (카테고리이거나 금액이 있거나 자식이 있음)
      if (!hasValidCategory || (!item.isCategory && !hasAmount && !hasChildren)) {
        return null;
      }
      
      const result: any = {
        id: item.id > 0 ? item.id : undefined,
        no: item.no, // NO 필드 추가
        category: item.category.trim(),
        amount: item.amount || 0,
        originalAmount: item.originalAmount,
        currency: item.currency || 'KRW',
        exchangeRate: item.exchangeRate,
        note: item.note?.trim() || '',
        type: item.type,
        isCategory: item.isCategory || hasChildren,
        level: item.level ?? (parentLevel + 1),
        sortOrder: item.sortOrder ?? index,
        parentId: item.parentId || null,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        _tempId: item.id < 0 ? item.id : undefined
      };
      
      // 자식 항목들 처리
      if (hasChildren) {
        const validChildren = item.children!
          .map((child, childIndex) => processItem(child, childIndex, item.level ?? parentLevel + 1))
          .filter(child => child !== null);
        
        if (validChildren.length > 0) {
          result.children = validChildren;
        } else {
          return null;
        }
      }
      
      return result;
    };
    
    return items
      .map((item, index) => processItem(item, index))
      .filter(item => item !== null);
  };

  const validIndirect = processHierarchicalData(data.indirect || []);
  const validDirect = processHierarchicalData(data.direct || []);

  const result = {
    employeeCount: data.employeeCount,
    indirect: validIndirect,
    direct: validDirect,
    deleteIds: [] // 삭제는 별도로 즉시 처리되므로 빈 배열
  };
  return result;
};

/**
 * 월별 데이터 배열에서 특정 월 데이터를 업데이트하는 유틸리티 함수
 */
export const updateMonthInArray = (
  monthlyData: MonthlyOpexData[], 
  updatedMonth: MonthlyOpexData
): MonthlyOpexData[] => {
  return monthlyData.map(data => 
    data.month === updatedMonth.month ? updatedMonth : data
  );
};